import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { Account, IAccount } from '@services/accounts.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { WebAppUser, AuthenticationService } from '@services/authentication.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { Subject, combineLatest, takeUntil, distinctUntilChanged, filter, finalize, take, firstValueFrom } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AssetDistribution, ModelsService, ModelType } from '@services/models.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { convertEpochToDateString } from '@functions/functions';
import { CreateAssetDistributionComponent } from '@dialogs/create-asset-distribution/create-asset-distribution.component';
import { MatSelect } from '@angular/material/select';
import { HttpErrorResponse } from '@angular/common/http';
import { AssetModelsComponent } from '@dialogs/asset-models/asset-models.component';
import { IQueryParameterFiltering, QueryParamType } from '@global/iFiltering';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** Used to filter the table of models */
export interface FilterQueryParams {
	Automatic?: boolean;
	Manual?: boolean;
	Active?: boolean;
	Disabled?: boolean;
	Enabled?: boolean;

	SelectedTab?: string;

	ModelType?: number;

	ModelsFilter?: string;
	ModelspageIndex?: number;
	ModelspageSize?: number;

	ModelsSortColumn?: string;
	ModelsSortDirection?: SortDirection;

	AssetsFilter?: string;
	AssetspageIndex?: number;
	AssetspageSize?: number;

	AssetsSortColumn?: string;
	AssetsSortDirection?: SortDirection;

	Location?: string;
}

/**
 * ModelsComponent
 */
@Component({
	selector: 'app-models',
	templateUrl: './models.component.html',
	styleUrls: ['./models.component.scss']
})
export class ModelsComponent implements OnInit, IQueryParameterFiltering<FilterQueryParams> {


	// =========================================================================
	// Query Parameter Filtering
	// =========================================================================

	/** 
	 * Query  Parameter List to use for filtering
	 * 
	 * filter Query Params available to use
	 */
	public filterQueryParams: FilterQueryParams = {}

	/** 
	 * filterQueryParamTypes 
	 * 
	 * Deffenition of the Query Parameter data types
	 */
	public filterQueryParamTypes: Record<keyof FilterQueryParams, QueryParamType> = {

		Automatic: 'boolean',
		Manual: 'boolean',
		Active: 'boolean',
		Disabled: 'boolean',
		Enabled: 'boolean',

		SelectedTab: 'string',
		Location: 'string',

		ModelType: 'number',

		ModelsFilter: 'string',
		ModelspageIndex: 'number',
		ModelspageSize: 'number',

		ModelsSortColumn: 'string',
		ModelsSortDirection: 'string',

		AssetsFilter: 'string',
		AssetspageIndex: 'number',
		AssetspageSize: 'number',

		AssetsSortColumn: 'string',
		AssetsSortDirection: 'string'

	};


	// =========================================================================
	// Component State
	// =========================================================================

	/** hasFilters */
	public hasFilters: boolean = false;

	/** selectedTabIndex */
	public selectedTabIndex: number = 0

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** accountID */
	public accountGUID: string;
	/** selectedAccount */
	public selectedAccount: IAccount;
	/** OrganisationID */
	public OrganisationUID: string;


	// =========================================================================
	// Table Configuration
	// =========================================================================

	/** displayedColumns */
	displayedColumns: string[] = ['AssetName', 'CreatedAt', 'StartPoint', 'EndPoint', 'Type', 'CreatedBy', 'MostRecent', 'Options', 'GoToAsset']

	/** displayedAssetColumns */
	displayedAssetColumns: string[] = [
		'Name',
		'Description',
		'HierarchyPath',
		'Buttons',
		'GoToAsset'
	];

	/** filteredAssetColumns */
	filteredAssetColumns: string[] = this.displayedAssetColumns

	/** datasource */
	dataSourceModels: MatTableDataSource<AssetDistribution>;
	modelsReturned: boolean = false;

	/** organisationAssets */
	public organisationAssets: LevelAsset[] = [];
	/** organisationAssetsNoModels */
	public organisationAssetsNoModels: LevelAsset[] = [];
	/** datasource */
	dataSourceAssets: MatTableDataSource<LevelAsset>;
	assetsReturned: boolean = false;


	// =========================================================================
	// Paginator and Sort Configuration
	// =========================================================================

	private _paginatorModels: MatPaginator;

	@ViewChild('paginatorModels')
	set paginatorModels(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }
			this._paginatorModels = paginator;
			if (this.dataSourceModels) {
				this.dataSourceModels.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Models paginator:', error);
		}
	}

	private _sortModels: MatSort;

	@ViewChild('sortModels')
	set sortModels(sort: MatSort) {
		try {
			if (!sort) { return; }

			if (this.filterQueryParams.ModelsSortColumn) { sort.active = this.filterQueryParams.ModelsSortColumn }
			if (this.filterQueryParams.ModelsSortDirection) { sort.direction = this.filterQueryParams.ModelsSortDirection }

			this._sortModels = sort;

			if (this.dataSourceModels) {
				this.dataSourceModels.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Models sort:', error);
		}
	}

	private _paginatorAssets: MatPaginator;

	@ViewChild('paginatorAssets')
	set paginatorAssets(paginator: MatPaginator) {
		try {
			if (!paginator) { return; }
			this._paginatorAssets = paginator;
			if (this.dataSourceAssets) {
				this.dataSourceAssets.paginator = paginator;
			}
		} catch (error) {
			console.error('Error assigning Assets paginator:', error);
		}
	}

	private _sortAssets: MatSort;

	@ViewChild('sortAssets')
	set sortAssets(sort: MatSort) {
		try {
			if (!sort) { return; }

			if (this.filterQueryParams.AssetsSortColumn) { sort.active = this.filterQueryParams.AssetsSortColumn }
			if (this.filterQueryParams.AssetsSortDirection) { sort.direction = this.filterQueryParams.AssetsSortDirection }

			this._sortAssets = sort;

			if (this.dataSourceAssets) {
				this.dataSourceAssets.sort = sort;
			}
		} catch (error) {
			console.error('Error assigning Assets sort:', error);
		}
	}


	// =========================================================================
	// User / Hierarchy / Location State
	// =========================================================================

	/** currentUser */
	public currentUser: WebAppUser;

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	/** hierarchyBreadcrumbs */
	private hierarchyBreadcrumbs: Map<number, string>;

	// =========================================================================
	// Location Dropdown
	// =========================================================================

	/** dropdownLocations */
	dropdownLocations: OrganisationsHierarchy[] = [];

	/** dropdownLocationFields */
	dropdownLocationFields: { text: string, value: string } = {
		text: 'Name',
		value: 'Uid'
	};

	private _locationDropdown: DropDownList;
	/** locationDropdown */
	@ViewChild('locationDropdown')
	set locationDropdown(dropdown: DropDownList) {
		try {
			if (!dropdown) { return; }

			this._locationDropdown = dropdown;
			dropdown.showClearButton = true;
		} catch (error) {
			console.error('Error assigning location Dropdown:', error);
		}
	}


	// =========================================================================
	// Model Configuration
	// =========================================================================

	/** modelTypeDropdown */
	@ViewChild('dropdownModelTypes') modelTypeDropdown: MatSelect;

	/** List of Tab IDs */
	public tabIDList: string[] = ["Model", "NoModel"]

	/** modelTypes */
	public modelTypes: ModelType[] = [];

	/** locationFields */
	public modelTypeFields: { text: string, value: string } = { text: "ModelName", value: "ModelTypeID" };

	/** assetDistributions */
	public assetDistributions: AssetDistribution[] = [];

	/** filteredAssetDistributions */
	public filteredAssetDistributions: AssetDistribution[] = [];

	/** models */
	public models: any[] = [];

	/** Table fields */
	public modelFields: Object = { text: 'RuleDescription', id: 'RuleID' };

	/** Table fields */
	public assetFields: Object = { text: 'Name', id: 'AssetID' };

	/** hierarchyAssetIDs */
	private hierarchyAssetIDs: Set<any>;

	/** filterLocationAssetIDs */
	flattenedAssetIDs: number[] = [];

	/** location Assets array */
	locationAssets: LevelAsset[] = [];

	/** locationHierarchyFilter */
	public locationHierarchyFilter?: OrganisationsHierarchy;

	/** selectedModelType */
	public selectedModelType: ModelType;


	// =========================================================================
	// Constructor
	// =========================================================================

	/**
	 * Default Constructor
	 * @param assetsDataService assetsDataService
	 * @param dialog dialog
	 * @param authenticationService authenticationService
	 * @param siteSubscriptions siteSubscriptions
	 * @param router router
	 * @param route route
	 * @param globalFunctions globalFunctions
	 * @param modelsService modelsService
	 */
	constructor(
		public assetsDataService: AssetsDataService,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions,
		public modelsService: ModelsService
	) { }


	// =========================================================================
	// Lifecycle
	// =========================================================================

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {
		this.globalFunctions.displaySpinner(true);

		combineLatest([
			this.siteSubscriptions.hierarchicalData$.pipe(),
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.route.queryParamMap.pipe(take(1)),
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([hierarchicalData, currentUser, queryParamMap]) =>
				!!hierarchicalData && !!currentUser && !!queryParamMap),
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: async ([hierarchicalData, currentUser, queryParamMap]) => {

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					]
					)
				);

				this.accountGUID = selectedAccountGUID;
				this.selectedAccount = selectedAccount;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.OrganisationUID = selectedOrganisationUID;

				let urlAccountID = this.router.url.split('/')[1];

				if (this.accountGUID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('models') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				this.currentUser = currentUser;

				this.hierarchy = JSON.parse(hierarchicalData);
				this.dropdownLocations = [...(this.hierarchy[0].SubChild ?? [])];

				this.hierarchyBreadcrumbs = this.flattenAssetBreadcrumbs(this.hierarchy);
				this.hierarchyAssetIDs = new Set(this.flattenAssets(this.hierarchy).map(a => a.AssetID));

				/** Default to look for only open cases */
				this.filterQueryParams = this.globalFunctions.queryParamsToObject<FilterQueryParams>(
					queryParamMap,
					this.filterQueryParamTypes
				);

				this.selectedTabIndex = this.tabIDList.indexOf(this.filterQueryParams.SelectedTab ?? '');

				this.locationHierarchyFilter = this.dropdownLocations.find(
					subchild => subchild.Uid == this.filterQueryParams.Location
				) ?? this.hierarchy[0];


				if (this.locationHierarchyFilter) {
					this.locationAssets = this.flattenAssets([this.locationHierarchyFilter]);
					this.flattenedAssetIDs = this.locationAssets.map(asset => asset.AssetID);
				}

				await this.getModelTypes()
				this.filterQueryParams.ModelType = this.filterQueryParams.ModelType ?? 1;
				if (this.modelTypes && this.filterQueryParams.ModelType) {
					this.selectedModelType = this.modelTypes.find(m => m.ModelTypeID == this.filterQueryParams.ModelType) ?? this.modelTypes[0]
				}

				/** Assets needed for the next requests */
				await this.getAssets();

				if (this.filterQueryParams.ModelType == 1) {
					await this.getDistributionModels()
				}

				this.applyQueryParameters("All", false);

				this.globalFunctions.displaySpinner(false);

			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				this.globalFunctions.displaySpinner(false);
			}
		});
	}


	// =========================================================================
	// Data Loading
	// =========================================================================

	/**
	 * getModelTypes
	 */
	async getModelTypes() {

		try {
			const modelTypes = await firstValueFrom(
				this.modelsService.getModelTypes$(this.accountGUID, this.OrganisationUID).pipe(
					filter(result => !!result)
				)
			);
			if (modelTypes) {
				this.modelTypes = modelTypes;
			}
		}
		catch (error: any) {
			if (error instanceof HttpErrorResponse && error.status === 404) { return; }
			this.globalFunctions.HandleError(error, "Error: this.modelsService.getModelTypes$", false);
		}

	}

	/**
	 * getDistributionModels
	 */
	async getDistributionModels() {

		try {
			const distributions = await firstValueFrom(
				this.modelsService.getAssetDistribution$(this.accountGUID, this.OrganisationUID)
					.pipe(
						filter(result => !!result)
					)
			);
			if (distributions) {
				this.assetDistributions = distributions.map((x: AssetDistribution) => ({
					...x,
					AssetName: this.getAssetName(x.AssetID)
				}));
				this.models = this.assetDistributions;
			}
			this.modelsReturned = true;

		}
		catch (error: any) {
			if (error instanceof HttpErrorResponse && error.status === 404) {
				this.assetDistributions = [];
				this.models = this.assetDistributions;
				this.modelsReturned = true; //count empty model list as models returned
				return;
			}
			this.globalFunctions.HandleError(error, "Error: this.modelsService.getAssetDistribution$", false);
		} finally {
			this.assignDistributionModelsToDataSource()
		}
	}

	/**
	 * get the Organisation Assets
	 */
	async getAssets() {

		try {

			const assets = await firstValueFrom(
				this.assetsDataService.getOrganisationAssets$(this.accountGUID, this.OrganisationUID)
					.pipe(
						filter((result) => !!result)
					)
			);

			if (assets) {
				this.organisationAssets = (<LevelAsset[]>assets)
				this.organisationAssetsNoModels = this.organisationAssets.filter((x: LevelAsset) => x.ModelCount === 0)
					.map((x: LevelAsset) => {
						x.HierarchyPath = this.hierarchyBreadcrumbs.get(x.AssetID) ?? '';
						return x;
					});
				this.assetsReturned = true;
			}
			else {
				this.globalFunctions.DisplaySnackBar({
					message: "Error Getting Asset List",
					duration: 5000,
					action: ""
				});
			}

			this.assetsReturned = true;

		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, "this.assetsDataService.getOrganisationAssets$");
			}
		} finally {
			this.assignAssetsToDataSource();
		}
	}


	// =========================================================================
	// Hierarchy / Asset Helpers
	// =========================================================================

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * Getting list of assets from an input hierarchy 
	 * @param hierarchy OrganisationsHierarchy
	 * @returns 
	 */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * Returns a map of AssetID -> Hierarchy breadcrumb
	 */
	flattenAssetBreadcrumbs(
		hierarchy: OrganisationsHierarchy[],
		path: string[] = [],
		result = new Map<number, string>()
	): Map<number, string> {

		if (!Array.isArray(hierarchy)) {
			return result;
		}

		for (const item of hierarchy) {

			const currentPath = [...path, item.Name];

			// Store breadcrumb for every asset at this level
			for (const asset of item.Assets ?? []) {
				result.set(asset.AssetID, currentPath.join(' > '));
			}

			// Recurse into children
			this.flattenAssetBreadcrumbs(
				item.SubChild,
				currentPath,
				result
			);
		}

		return result;
	}

	/**
	 * findAsset
	 * @param assetID assetID  
	 * @returns 
	 */
	getAssetName(assetID: number): string {
		let asset = this.organisationAssets.find((x: LevelAsset) => x.AssetID == assetID);
		if (asset) {
			return `${asset.Name} - ${asset.Description}`;
		} else {
			return "";
		}
	}


	// =========================================================================
	// Query Parameter Handling
	// =========================================================================

	/**
	 * Applies the current query parameters to the component's data.
	 *
	 * @param page - The page or data set being queried.
	 * @param reQuery - Determines whether the data should be queried again.
	 */
	applyQueryParameters(page: string = "All", reQuery: boolean = false) {

		if (page == "All" || page == this.tabIDList[0]) {
			const currentFilter = this.dataSourceModels.filter;
			this.dataSourceModels.filter = '';
			this.dataSourceModels.filter = currentFilter || this.filterQueryParams.ModelsFilter || ' ';
		}

		if (page == "All" || page == this.tabIDList[1]) {
			const currentFilter = this.dataSourceAssets.filter;
			this.dataSourceAssets.filter = '';
			this.dataSourceAssets.filter = currentFilter || this.filterQueryParams.AssetsFilter || ' ';
		}

		this.rebuildURL(reQuery);

	}

	/**
	 * Rebuilds the URL using the current query parameter values.
	 *
	 * @param reQuery - Determines whether the data should be queried again
	 * after the URL has been rebuilt.
	 */
	rebuildURL(reQuery: boolean): void {
		if (reQuery) {
			// this.router.navigate([], { queryParams: this.filterData, queryParamsHandling: 'merge' });
			this.router.navigate([], { queryParams: this.filterQueryParams });
		} else {
			const urlTree = this.router.createUrlTree([], {
				queryParams: this.filterQueryParams,
				queryParamsHandling: 'merge'
			});

			window.history.replaceState(
				window.history.state,
				'',
				this.router.serializeUrl(urlTree)
			);
		}

	}

	/**
		 * Handles a change to a query parameter.
		 *
		 * @param changed - The name of the query parameter that has changed.
		 */
	onQueryParameterChange(changed: keyof typeof this.filterQueryParams, event: any) {

		this.filterQueryParams.SelectedTab ?? "All";

		if (changed == "ModelsFilter") {
			event.cancel = true;
			const query = (event.target as HTMLInputElement).value.toLowerCase();
			this.filterQueryParams.ModelsFilter = query;
			this.dataSourceModels.filter = query;
		}
		else if (changed == "AssetsFilter") {
			event.cancel = true;
			const query = (event.target as HTMLInputElement).value.toLowerCase();
			this.filterQueryParams.AssetsFilter = query;
			this.dataSourceAssets.filter = query;
		}
		else if (changed == "SelectedTab") {
			this.selectedTabIndex = event.selectedIndex;
			let tabName = this.tabIDList[event.selectedIndex] ?? "";
			this.filterQueryParams.SelectedTab = tabName;
		}
		else if (changed == "ModelsSortColumn") {
			this.filterQueryParams.ModelsSortColumn = event.active;
			this.filterQueryParams.ModelsSortDirection = event.direction;
		}
		else if (changed == "AssetsSortColumn") {
			this.filterQueryParams.AssetsSortColumn = event.active;
			this.filterQueryParams.AssetsSortDirection = event.direction;
		}

		/** Update the Selected Tab */
		let selectedTab = this.filterQueryParams.SelectedTab;

		if (changed == "ModelType") {
			this.selectedModelType = event.value;
			this.filterQueryParams.ModelType = this.selectedModelType.ModelTypeID;
		}

		if (changed == "Location") {

			if (this.locationDropdown) {
				this.locationDropdown.showClearButton = true;
			}

			if (event.itemData) {
				this.locationHierarchyFilter = <OrganisationsHierarchy>event.itemData;
				this.filterQueryParams.Location = event.value;
			}
			else if (!event.itemData) {
				this.locationHierarchyFilter = this.hierarchy[0];
				this.filterQueryParams.Location = undefined;
			}

			if (this.locationHierarchyFilter) {
				this.organisationAssets = this.flattenAssets([this.locationHierarchyFilter]);
				this.flattenedAssetIDs = this.organisationAssets.map(asset => asset.AssetID);
			}

			/** Update all the queries */
			selectedTab = "All";

		}

		this.applyQueryParameters(selectedTab, false);
	}



	// =========================================================================
	// Pagination
	// =========================================================================

	/**
	 * onPageChange
	 */
	onPageChange(pag: MatPaginator, event: PageEvent): void {

		if (pag == this._paginatorModels) {
			this.filterQueryParams.ModelspageSize = pag.pageSize;
			this.filterQueryParams.ModelspageIndex = pag.pageIndex;
		}

		if (pag == this._paginatorAssets) {
			this.filterQueryParams.AssetspageSize = pag.pageSize;
			this.filterQueryParams.AssetspageIndex = pag.pageIndex;
		}

		this.applyQueryParameters("All", false)
	}


	// =========================================================================
	// Data Source Configuration
	// =========================================================================

	/**
	 * assignModelsToDataSource
	 */
	assignDistributionModelsToDataSource() {

		if (!this.dataSourceModels) {
			this.dataSourceModels = new MatTableDataSource(this.assetDistributions);
		} else {
			this.dataSourceModels.data = this.assetDistributions;
		}

		this.dataSourceModels.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'CreatedByUserSub': return item.CreatedByUserSub || '';
				case 'AssetName': return item.AssetName || '';
				case 'CreatedAt': return item.CreatedAt || '';
				case 'StartPoint': return item.StartPoint || '';
				case 'EndPoint': return item.EndPoint || '';
				default: return '';
			}
		};

		this.dataSourceModels.filterPredicate = (model: AssetDistribution, filter: string) => {

			/* Remove any Leading or following Spaces */
			filter = filter.trim();

			if (
				(this.filterQueryParams.Automatic && !model.IsAutomatic) ||
				(this.filterQueryParams.Manual && model.IsAutomatic) ||
				(this.filterQueryParams.Active && !model.MostRecent) ||
				(this.filterQueryParams.Disabled && model.Disabled) ||
				(this.filterQueryParams.Enabled && !model.Disabled)) {
				return false;
			}

			if (this.locationHierarchyFilter) {
				if (!this.flattenedAssetIDs.includes(model.AssetID)) {
					return false;
				}
			}

			if (!filter) { return true; }

			let name: string = "";
			let asset = this.organisationAssets.find((y: LevelAsset) => y.AssetID == model.AssetID);
			if (asset) { name = `${asset.Name}${asset.Description}`; }
			let createdBy: string = model.CreatedByUserSub;
			let createdAt: string = this.convertEpoch(model.CreatedAt);
			let startPoint: string = this.convertEpoch(model.StartPoint);
			let endPoint: string = this.convertEpoch(model.EndPoint);
			const textToSearch = `${name}${createdBy}${createdAt}${startPoint}${endPoint}`.toLowerCase();

			return textToSearch.includes(filter);
		};

	}

	/**
	 * assignAssetsToDataSource
	 */
	assignAssetsToDataSource() {

		if (!this.dataSourceAssets) {
			this.dataSourceAssets = new MatTableDataSource(this.organisationAssetsNoModels);
		} else {
			this.dataSourceAssets.data = this.organisationAssetsNoModels;
		}

		this.dataSourceAssets.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'Name': return item.Name || '';
				case 'Description': return item.Description || '';
				case 'HierarchyPath': return item.HierarchyPath || '';
				default: return '';
			}
		};

		this.dataSourceAssets.filterPredicate = (asset: LevelAsset, filter: string) => {

			/* Remove any Leading or following Spaces */
			filter = filter.trim();

			if (this.locationHierarchyFilter) {
				if (!this.flattenedAssetIDs.includes(asset.AssetID)) {
					return false;
				}
			}

			if (!filter) { return true; }

			const textToSearch =
				`${asset.Name ?? ''}${asset.Description ?? ''}`.toLowerCase();

			return textToSearch.includes(filter);
		};

	}


	// =========================================================================
	// Filtering
	// =========================================================================

	/**
	 * setFilterOption
	 * @param option option 
	 * @param value value 
	 */
	setFilterOption(option: string, value: boolean) {
		switch (option) {
			case 'automatic only':
				this.filterQueryParams.Automatic = value;
				break;
			case 'manual only':
				this.filterQueryParams.Manual = value;
				break;
			case 'most recent':
				this.filterQueryParams.Active = value;
				break;
			case 'disabled':
				this.filterQueryParams.Disabled = value;
				break;
			case 'enabled':
				this.filterQueryParams.Enabled = value;
				break;
			default:
				break;
		}
		this.applyQueryParameters(undefined, false)
	}


	// =========================================================================
	// Model Actions / Navigation
	// =========================================================================

	/**
	 * goToModel
	 * @param model model 
	 */
	goToModel(model: any) {
		this.router.navigate([`./${this.accountGUID}/organisation/${this.OrganisationUID}/models/${model.AssetDistributionID}`],
			{ queryParams: { 'modelType': this.selectedModelType.ModelTypeID } });
		return;
	}

	/**
	 * findAsset
	 * @param assetID assetID  
	 * @returns 
	 */
	getAsset(assetID: number): LevelAsset | undefined {
		return this.organisationAssets.find((x: LevelAsset) => x.AssetID == assetID);
	}

	/**
	 * disableModel
	 * @param model model  
	 * @param enable enable
	 */
	disableModel(model: any, enable?: boolean) {
		this.globalFunctions.UserConfirm$("Are you sure you want to delete this model?").subscribe(async OKResult => {
			if (OKResult) {
				if (this.selectedModelType.ModelTypeID == 1) {
					// Create a local Subject to control the subscription lifecycle
					const fetchDataSubject = new Subject<void>();

					this.modelsService.patchOldDistribution$(this.accountGUID, this.OrganisationUID, model.AssetDistributionID, model.AssetID, false, enable == true ? false : true).pipe(
						takeUntil(fetchDataSubject), distinctUntilChanged(),
						filter((response) => !!response),
						finalize(() => {
							// Ensure the subscription is terminated and the subject is completed
							fetchDataSubject.next();
							fetchDataSubject.complete();
							this.getDistributionModels();
						})
					).subscribe({
						next: (response) => {
							this.globalFunctions.DisplaySnackBar({
								message: enable == true ? "Model enabled" : "Model deleted",
								duration: 2000,
								action: ""
							})
							this.getDistributionModels();
						},
						error: (error) => { this.globalFunctions.HandleError(error, "Error: this.modelsService.patchOldDistribution$", false); }
					});
				}
			}
		});
	}

	/**
	 * activateModel
	 * @param model model  
	 * @param deactivate deactivate 
	 */
	activateModel(model: any, deactivate: boolean = false) {
		if (this.selectedModelType.ModelTypeID == 1) {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();

			this.modelsService.patchOldDistribution$(this.accountGUID, this.OrganisationUID, model.AssetDistributionID, model.AssetID, deactivate ? false : true, false).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.getDistributionModels();
				})
			).subscribe({
				next: (response) => {
					this.globalFunctions.DisplaySnackBar({
						message: deactivate ? "Model deactivated" : "Model activated",
						duration: 2000,
						action: ""
					})
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.modelsService.patchOldDistribution$", false); }
			});
		}
	}

	/**
	 * recreateModel
	 * @param model model 
	 */
	recreateModel(model: any) {
		if (this.selectedModelType.ModelTypeID == 1) {
			let data = {
				asset: this.getAsset(model.AssetID),
				viewing: false,
				editing: true,
				distributionID: model.AssetDistributionID,
				existingDistribution: model
			}

			const dialogRef = this.dialog.open(CreateAssetDistributionComponent
				, { data: data }
			);

			dialogRef.afterClosed().pipe(
				filter(result => !!result)
			).subscribe(result => {
				this.patchDistribution(model, result.tolerance, result.sensitivity, result.recreationFrequency, result.overlapPeriod);
			});
		}
	}

	/**
	 * patchDistribution
	 * @param distribution distribution
	 * @param tolerance tolerance 
	 * @param sensitivity sensitivity 
	 * @param recreationFrequency recreationFrequency 
	 * @param overlapPeriod overlapPeriod 
	 */
	patchDistribution(distribution: AssetDistribution, tolerance: number, sensitivity: number, recreationFrequency: number, overlapPeriod: number) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.modelsService.patchDistribution$(this.accountGUID, this.OrganisationUID, distribution.AssetDistributionID,
			tolerance, sensitivity, recreationFrequency, overlapPeriod).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (result) => {
					this.globalFunctions.DisplaySnackBar({
						message: "Asset Distribution Updated",
						type: SnackType.Info,
						duration: 1000
					});
					this.siteSubscriptions.UpdateModelList(true);
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postAssetDistribution$", false); },
			});
	}

	/**
	 * goToAsset
	 * @param row 
	 */
	goToAsset(row: any) {
		let asset = this.getAsset(row.AssetID);
		if (asset) {
			this.siteSubscriptions.SetSelectedAsset(asset);
			this.router.navigate([`./${this.accountGUID}/organisation/${this.OrganisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`]);
		}
	}

	// /**
	//  * clearAllFilters
	//  */
	// clearAllFilters() {
	// 	this.filterQueryParams = {
	// 		Automatic: false,
	// 		Manual: false,
	// 		Active: false
	// 	}
	// 	this.locationHierarchyFilter = undefined;
	// 	this.filterByLocation(undefined, true);
	// 	this.applyQueryParameters("All", false);
	// }


	// =========================================================================
	// Filter / Display Messages
	// =========================================================================

	/**
	 * getFilterMessage
	 * @returns 
	 */
	getFilterMessage(): string {
		this.hasFilters = false;
		let showingResult: string = "Showing ";
		let amount: number = 0;
		let hasLocation: boolean = false;
		Object.entries(this.filterQueryParams).forEach(([key, value]) => {
			if (value) {
				this.hasFilters = true;
				if (key != "Location") {
					if (amount == 0) {
						showingResult += " " + key
					} else {
						showingResult += ", " + key
					}
					amount++;
				}
			}
		});
		if (this.locationHierarchyFilter) {
			hasLocation = true;
		}
		if (amount == 0) {
			return showingResult + (hasLocation ? `all models in ${this.locationHierarchyFilter?.Name}` : "all models");
		}
		return showingResult + (hasLocation ? " models in " + this.locationHierarchyFilter?.Name : " models");
	}

	/**
	 * getLocationMessage
	 * @returns 
	 */
	getLocationMessage(): string {
		let result: string = "Showing ";
		let hasLocation: boolean = false;
		if (this.locationHierarchyFilter) {
			hasLocation = true;
		}
		return result + (hasLocation ? "Assets in " + this.locationHierarchyFilter?.Name : "All Assets");
	}


	// =========================================================================
	// Dialog Actions
	// =========================================================================

	/**
	 * ManageModels
	 * Open Models List as a pop up dialog and manage models for the selected asset based on the result
	 */
	ManageModels(selectedAsset: LevelAsset) {

		let data = {
			asset: selectedAsset
		}

		const dialogRef = this.dialog.open(AssetModelsComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			this.getDistributionModels();
		});
	}

}
